import { BaseQueryFn, FetchArgs, FetchBaseQueryError } from '@reduxjs/toolkit/query/react';
import { Mutex } from 'async-mutex';

import { logout, setAccessToken } from '@app/store/slices/auth/authSlice';
import { RootState } from '@app/store/store';
import { baseQuery } from './baseQuery';

const mutex = new Mutex();
const AUTH_BOOTSTRAP_TIMEOUT_MS = 8000;
const AUTH_BOOTSTRAP_POLL_MS = 50;
const AUTH_URLS = ['/auth/login', '/auth/logout', '/auth/refresh', '/auth/telegram-login'];
const PUBLIC_URLS = ['/users/total', '/log/fe', '/meta.json'];

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const getRequestUrl = (args: string | FetchArgs) => (typeof args === 'string' ? args : args.url);

const isAuthBootstrapRequest = (url: string) => AUTH_URLS.some((path) => url.includes(path));

const shouldWaitForAuthBootstrap = (url: string) =>
  !isAuthBootstrapRequest(url) && !PUBLIC_URLS.some((path) => url.includes(path));

const isUnauthorized = (error: FetchBaseQueryError | undefined) =>
  error?.status === 401 || (error as Record<string, number> | undefined)?.originalStatus === 401;

const waitForAuthBootstrap = async (api: Parameters<BaseQueryFn>[1], url: string) => {
  if (!shouldWaitForAuthBootstrap(url)) {
    return;
  }

  let elapsed = 0;
  while (
    (api.getState() as RootState).auth.isBootstrapping &&
    elapsed < AUTH_BOOTSTRAP_TIMEOUT_MS
  ) {
    await sleep(AUTH_BOOTSTRAP_POLL_MS);
    elapsed += AUTH_BOOTSTRAP_POLL_MS;
  }
};

export const baseQueryWithReauth: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  const requestUrl = getRequestUrl(args);

  await waitForAuthBootstrap(api, requestUrl);

  await mutex.waitForUnlock();

  let result = await baseQuery(args, api, extraOptions);

  if (isUnauthorized(result.error)) {
    if (
      shouldWaitForAuthBootstrap(requestUrl) &&
      (api.getState() as RootState).auth.isBootstrapping
    ) {
      await waitForAuthBootstrap(api, requestUrl);
      result = await baseQuery(args, api, extraOptions);
    }

    if (!isUnauthorized(result.error)) {
      return result;
    }

    // если уже выполняется refresh
    if (!mutex.isLocked()) {
      const release = await mutex.acquire();

      try {
        const refreshResult = await baseQuery(
          {
            url: '/auth/refresh',
            method: 'POST',
          },
          api,
          extraOptions
        );

        if (refreshResult.data) {
          const authHeader = refreshResult.meta?.response?.headers.get('authorization');
          const token = authHeader?.replace('Bearer ', '');
          if (token) {
            api.dispatch(setAccessToken(token));
            // повторяем оригинальный запрос
            result = await baseQuery(args, api, extraOptions);
          }
        } else {
          api.dispatch(logout());
        }
        // } catch {
        //   api.dispatch(logout());
      } finally {
        release();
      }
    } else {
      // если refresh уже выполняется - ждем
      await mutex.waitForUnlock();

      // выполняем запрос
      result = await baseQuery(args, api, extraOptions);
    }
  }

  return result;
};
